import {
  Activity,
  Archive,
  ArrowLeftRight,
  BookOpen,
  Box,
  CalendarDays,
  CalendarRange,
  Code,
  Contact,
  Eye,
  FileSignature,
  FileText,
  Globe,
  Image,
  KeyRound,
  Layers,
  LayoutDashboard,
  Mail,
  Monitor,
  PenLine,
  QrCode,
  Receipt,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Store,
  Tag,
  Upload,
  User,
  Users,
  type LucideIcon,
} from 'lucide-react';
import {
  definitionModule,
  estModuleCoeur,
  type Module,
  type ModuleCoeur,
  type ModuleOptionnel,
  type PalierModule,
} from '@artosera/shared';

/**
 * Le classement de lecture de la carte des modules (maquette Halo « cartes par
 * palier ») : les catégories d'affichage du registre, plus « Socle » pour le
 * cœur qui n'a pas de catégorie. Un classement d'affichage : il n'a aucun
 * effet sur l'accès.
 */
export const FAMILLES_CARTE = [
  'oeuvres-documents',
  'artistes-contrats',
  'ventes-clients',
  'diffusion',
  'equipe-integrations',
  'socle',
] as const;
export type FamilleCarte = (typeof FAMILLES_CARTE)[number];

/** Où le registre range chaque module du cœur sur la carte. */
const FAMILLE_COEUR: Readonly<Record<ModuleCoeur, FamilleCarte>> = {
  'tableau-de-bord': 'socle',
  oeuvres: 'oeuvres-documents',
  'documents-express': 'oeuvres-documents',
  artistes: 'artistes-contrats',
  contacts: 'ventes-clients',
  expositions: 'diffusion',
  equipe: 'equipe-integrations',
  'deux-facteurs': 'socle',
  reglages: 'socle',
  export: 'socle',
  profil: 'socle',
  exploitation: 'socle',
  'conservation-legale': 'socle',
  etiquettes: 'oeuvres-documents',
};

/** La famille d'un module sur la carte : sa catégorie, ou « Socle » au cœur. */
export function familleDuModule(module: Module): FamilleCarte {
  if (estModuleCoeur(module)) return FAMILLE_COEUR[module];
  const definition = definitionModule(module);
  const categorie = 'categorie' in definition ? definition.categorie : undefined;
  return (FAMILLES_CARTE as readonly string[]).includes(categorie ?? '')
    ? (categorie as FamilleCarte)
    : 'socle';
}

/** L'icône de chaque module de la carte (add-ons exclus : ils n'y figurent pas). */
export const ICONES_MODULES: Partial<Record<Module, LucideIcon>> = {
  'tableau-de-bord': LayoutDashboard,
  oeuvres: Image,
  'documents-express': FileText,
  artistes: User,
  contacts: Contact,
  expositions: CalendarRange,
  equipe: Users,
  'deux-facteurs': ShieldCheck,
  reglages: Settings,
  export: Upload,
  profil: User,
  exploitation: Activity,
  'conservation-legale': Archive,
  etiquettes: QrCode,
  facturation: Receipt,
  'viewing-rooms': Eye,
  coffre: Box,
  'offres-relances': Tag,
  'mode-foire': Store,
  'documents-prestige': FileText,
  'modeles-contrats': FileSignature,
  mouvements: ArrowLeftRight,
  'editions-provenance': BookOpen,
  'contrats-artistes': PenLine,
  'agenda-presse': CalendarDays,
  'roles-fins': KeyRound,
  'newsletter-reseaux': Mail,
  'artsy-artnet': Globe,
  'application-hors-ligne': Smartphone,
  'api-comptabilite': Code,
  'console-groupe': Layers,
  sso: ShieldCheck,
  site: Monitor,
  boutique: ShoppingCart,
};

/**
 * La page d'un module ACTIF, relative à la galerie (`''` : l'accueil), ou
 * `null` quand il n'a pas d'écran propre (export, profil, exploitation…). Le
 * registre fait foi (`navigation`) ; ce tableau ne comble que les modules qui
 * n'ont pas d'entrée de navigation.
 */
const DESTINATION_SANS_NAVIGATION: Readonly<Partial<Record<Module, string | null>>> = {
  'documents-express': 'oeuvres',
  equipe: 'reglages/equipe',
  'deux-facteurs': 'reglages/securite',
  'editions-provenance': 'oeuvres',
  'roles-fins': 'reglages/equipe',
  sso: 'reglages/connecteurs',
  'console-groupe': 'groupe',
};

export function destinationDuModule(module: Module): string | null {
  const [premiere] = definitionModule(module).navigation;
  if (premiere !== undefined) return premiere;
  return DESTINATION_SANS_NAVIGATION[module] ?? null;
}

/**
 * Sur quels modules déjà là s'appuie un module à venir — de quoi dire « S'appuie
 * sur » en fiche. Une liste éditoriale (les dépendances strictes du registre
 * s'y ajoutent) : seuls ceux que la galerie a d'effectif sont montrés.
 */
export const APPUIS: Readonly<Record<ModuleOptionnel, readonly Module[]>> = {
  facturation: ['oeuvres', 'contacts', 'conservation-legale'],
  'viewing-rooms': ['oeuvres', 'contacts'],
  coffre: ['oeuvres', 'artistes', 'contacts'],
  'offres-relances': ['oeuvres', 'contacts'],
  'mode-foire': ['oeuvres', 'contacts'],
  'documents-prestige': ['oeuvres', 'documents-express'],
  'modeles-contrats': ['artistes', 'contacts'],
  mouvements: ['oeuvres', 'contacts', 'expositions'],
  'editions-provenance': ['oeuvres'],
  'contrats-artistes': ['artistes'],
  'agenda-presse': ['expositions', 'contacts'],
  'roles-fins': ['equipe'],
  'newsletter-reseaux': ['contacts'],
  'artsy-artnet': ['oeuvres', 'artistes'],
  'application-hors-ligne': ['oeuvres', 'contacts'],
  'api-comptabilite': ['conservation-legale', 'oeuvres'],
  'console-groupe': ['equipe', 'oeuvres'],
  sso: ['equipe', 'reglages'],
  site: ['oeuvres', 'artistes', 'expositions'],
  boutique: ['oeuvres', 'contacts'],
};

/** Le module phare de chaque palier, montré en aperçu fantôme sur le tableau de bord. */
export type FormeApercu = 'barres' | 'vignettes' | 'lignes';
export const APERCU_PALIER: Readonly<
  Record<Exclude<PalierModule, 'mvp'>, { module: ModuleOptionnel; forme: FormeApercu }>
> = {
  v1: { module: 'facturation', forme: 'barres' },
  v2: { module: 'viewing-rooms', forme: 'vignettes' },
  finale: { module: 'agenda-presse', forme: 'lignes' },
};
